import { fetchWithAuth } from '../utils/api';

// Defines a log entry group by date
export interface LogDateGroup<T> {
  date: string;
  logs: T[];
}

export interface ActivityLog {
  id: number;
  user: {
    id: number;
    username: string;
    first_name?: string;
    last_name?: string;
  };
  round: {
    id: number;
    format: string;
    motion?: {
      text: string;
      theme: string | null;
    };
  };
  role: string;
  action: string;
  timestamp: string;
}

export interface SystemLog {
  id: number;
  log_type: string;
  message: string;
  user: {
    id: number;
    username: string;
  } | null;
  activity: string;
  timestamp: string;
  metadata?: any;
}

export interface LogFilters {
  user_id?: number;
  round_id?: number;
  motion_id?: number;
  role?: string;
  action?: string;
  log_type?: string;
  activity?: string;
  message?: string;
  date_from?: string;
  date_to?: string;
}

// Helper to build query string
const buildQueryString = (filters: LogFilters): string => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value.toString());
    }
  });
  
  return params.toString();
};

export const fetchActivityLogs = async (filters: LogFilters = {}): Promise<LogDateGroup<ActivityLog>[]> => {
  try {
    const queryString = buildQueryString(filters);
    const endpoint = `/api/admin/logs/attendance/${queryString ? '?' + queryString : ''}`;
    const response = await fetchWithAuth(endpoint);
    return response || [];
  } catch (error) {
    console.error("Error fetching activity logs:", error);
    throw error;
  }
};

export const fetchSystemLogs = async (filters: LogFilters = {}): Promise<LogDateGroup<SystemLog>[]> => {
  try {
    const queryString = buildQueryString(filters);
    const endpoint = `/api/admin/logs/system/${queryString ? '?' + queryString : ''}`;
    const response = await fetchWithAuth(endpoint);
    return response || [];
  } catch (error) {
    console.error("Error fetching system logs:", error);
    throw error;
  }
};

export const exportActivityLogsCSV = async (filters: LogFilters = {}): Promise<void> => {
  try {
    // Add export=csv to filters
    const exportFilters = { ...filters, export: 'csv' };
    const queryString = buildQueryString(exportFilters);
    const endpoint = `/api/admin/logs/attendance/${queryString ? '?' + queryString : ''}`;
    
    // Use fetch directly to get the blob response
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');
    
    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Token ${token}`
      }
    });
    
    if (!response.ok) throw new Error(`Failed to export: ${response.statusText}`);
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `attendance_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error exporting activity logs:", error);
    throw error;
  }
};

export const exportSystemLogsCSV = async (filters: LogFilters = {}): Promise<void> => {
  try {
    // Add export=csv to filters
    const exportFilters = { ...filters, export: 'csv' };
    const queryString = buildQueryString(exportFilters);
    const endpoint = `/api/admin/logs/system/${queryString ? '?' + queryString : ''}`;
    
    // Use fetch directly to get the blob response
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');
    
    const response = await fetch(endpoint, {
      headers: {
        Authorization: `Token ${token}`
      }
    });
    
    if (!response.ok) throw new Error(`Failed to export: ${response.statusText}`);
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `system_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error exporting system logs:", error);
    throw error;
  }
};